import React, { useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PatternlibButton,
  PatternlibIcon,
  PatternlibToast,
  PatternlibMainNavigationItem,
} from "@liebherr/patternlib-react";
import { useTranslation } from "react-i18next";
import styles from "./Compare.module.css";
import BackButton from "../../components/back-button/BackButton";
import Separator from "../../components/separator/Separator";
import {
  useLocalStorage,
  copyToClipboard,
  pushToDataLayer,
} from "../../helpers/helpers";
import MainNavigation from "../../components/main-navigation/MainNavigation";
import Sidebar from "../../components/sidebar/Sidebar";
import useMutationObserver from "../../hooks/useMutationObserver";

const Compare = ({ isDemoUser }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const navigate = useNavigate();
  const [values, setValues] = useState([]);
  const [items, setItems] = useLocalStorage("saved", []);

  // use mutation observer to get current screen size
  const options = {
    config: { attributes: true, childList: true, subtree: true },
  };
  const target = document.documentElement;
  const attributeName = "breakpoint";
  const screen = useMutationObserver(options, target, attributeName);

  const [selectedForComparison, setSelectedForComparison] = useLocalStorage(
    "compare",
    []
  );

  const configOpenGA = () => {
    const eventName = "configuration_open";
    const pagePath = location.pathname;
    const pageType = "my_configurations";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    let props = { pagePath, pageType, language };

    pushToDataLayer(eventName, props);
  };

  // the useRef Hook allows to persist data between renders
  const toastRef = useRef();

  const searchForPart = (type, key) => {
    configOpenGA();
    switch (type) {
      case "S26":
        navigate("/series260", { state: { key: key } });
        break;
      case "S38":
        navigate("/series380", { state: { key: key } });
        break;
      case "ISO":
        navigate("/iso6022", { state: { key: key } });
        break;
      default:
        navigate("/series260", { state: { key: key } });
    }
  };

  const getPlaceholerImage = (key) => {
    switch (key) {
      case "S26":
        return "https://webapi.partcommunity.com/FileService/CatalogImage?PreviewPath=liebherr_components/series260/series260_asmtab.png&color=transparent&depth=32";
      case "S38":
        return "https://webapi.partcommunity.com/FileService/CatalogImage?PreviewPath=liebherr_components/series380/series380_asmtab.png&color=transparent&depth=32";
      case "ISO":
        return "https://webapi.partcommunity.com/FileService/CatalogImage?PreviewPath=liebherr_components/iso6022/iso6022_asmtab.png&color=transparent&depth=32";
      default:
        return "https://webapi.partcommunity.com/FileService/CatalogImage?PreviewPath=liebherr_components/series260/series260_asmtab.png&color=transparent&depth=32";
    }
  };

  const copyKeyToClipboard = (key) => async () => {
    const toastItem = await copyToClipboard(key, t("KeyButton.message"));
    if (toastItem !== null) {
      const toast = toastRef.current;
      toast.createToastItem({
        ...toastItem,
      });
    }
  };

  const getListItems = () => {
    const items = values.map((item, index) => {
      return (
        <div key={index} className={styles.detailsContainer}>
          <img
            className={styles.preview}
            src={
              item["image"] ? item["image"] : getPlaceholerImage(item["type"])
            }
            alt="img"
          ></img>
          <div className={styles.titleWrapper}>
            <h3 className={styles.title} title={item["customName"]}>
              {item["customName"]}
            </h3>
            <div className={styles.key}>
              <span>{item["part"]}</span>
              <PatternlibButton
                size="small"
                label={t("KeyButton.copy")}
                onClick={copyKeyToClipboard(item["part"])}
              ></PatternlibButton>
            </div>
            <div className={styles.customNotes}>
              <span title={item["customNotes"]}>
                {item["customNotes"]
                  ? item["customNotes"]
                  : t("compare.noNotes")}
              </span>
            </div>
          </div>
          <PatternlibButton
            width="auto"
            display="flex"
            type="secondary"
            label={t("compare.open")}
            onClick={() => searchForPart(item["type"], item["part"])}
            icon-position="right"
          >
            <PatternlibIcon icon-name="arrow-right" size="24px" slot="icon" />
          </PatternlibButton>

          <div className={styles.datalist}>{item["datalist"]}</div>
        </div>
      );
    });

    return <div className={styles.wrapper}>{items}</div>;
  };

  // Copy items and inject datalist to each item. Then update state.
  const injectItemsData = (items) => {
    // Filter saved items by selected items
    const result = items.filter((element) =>
      selectedForComparison.includes(element["id"])
    );

    const itemWithValues = result.map((item) => {
      const datalist = [];
      if (item["data"]) {
        let values = item["data"];
        let objectOrder;

        const expr = item["type"];
        switch (expr) {
          case "S38":
            objectOrder = {
              D: null,
              SD: null,
              TYP: null,
              MT: null,
              DE: null,
              EL: null,
              HUB: null,
              MAT: null,
              PA: null,
              FN1: null,
              FN2: null,
              DAM: null,
              SAE: null,
              COL: null,
            };
            break;
          case "S26":
            objectOrder = {
              D: null,
              SD: null,
              TYP: null,
              MT: null,
              CT: null,
              EL: null,
              HUB: null,
              PA: null,
              FN1: null,
              FN2: null,
              MC: null,
              V: null,
              COL: null,
            };
            break;
          case "ISO":
            objectOrder = {
              D: null,
              SD: null,
              TYP: null,
              MT: null,
              CT: null,
              EL: null,
              HUB: null,
              PA: null,
              FN1: null,
              FN2: null,
              DAM: null,
              SAE: null,
              SAEPOS: null,
              PS: null,
              V: null,
              COL: null,
            };
            break;
          default:
            objectOrder = { D: null, SD: null };
        }

        values = Object.assign(objectOrder, values);

        Object.keys(values).map(function (key) {
          const obj = values[key];
          const excludeKeys = ["POS", "SAEINFO", "P", "fixedD", "fixedSD"];
          if (!excludeKeys.includes(key)) {
            if (obj) {
              if (
                obj["status"] === "static" ||
                obj["status"] === "" ||
                obj["status"] === "ranged" ||
                obj["status"] === "fixed"
              ) {
                datalist.push(
                  <li key={key}>
                    <span className={styles.icons}></span>
                    <b>{obj["desc"]}</b>
                    <br></br>
                    {obj["resolvedVal"] + " " + obj["Unit"]}
                  </li>
                );
              }
            }
          }
          item["datalist"] = <ul className={styles.list}>{datalist}</ul>;
        });
      }
      return item;
    });
    setValues(itemWithValues);
  };

  useLayoutEffect(() => {
    // Reset selected items if item does not found in saved items
    let success = false;
    try {
      selectedForComparison.map((id) => {
        const index = items.findIndex((element) => element["id"] === id);
        success = true;

        if (index === -1) {
          success = false;
          throw new Error(t("compare.errorMessage"));
        }
      });
    } catch (error) {
      console.error(error);
      // If error reset the selected items
      setSelectedForComparison([]);

      // Create toast message
      const toastItem = {
        itemId: "2",
        message: error.message,
        type: "warning",
        autoHide: true,
      };

      const toast = toastRef.current;
      toast.createToastItem({
        ...toastItem,
      });
    } finally {
      if (success) {
        injectItemsData(items);
      }
    }
  }, []);

  const toPage = (tabId) => () => {
    switch (tabId) {
      case 0:
        navigate("/myconfigs");
        break;
      case 1:
        navigate("/contact");
        break;
      default:
        navigate("/");
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.navigation}>
          <MainNavigation activeTab={1}></MainNavigation>
        </div>
      </header>
      <div className={styles.body}>
        <div className={styles.sidebar}>
          <div className={styles.sidebarWrapper}>
            {screen === "isMobile" ? (
              <>
                <PatternlibMainNavigationItem is-active onClick={toPage(0)}>
                  {t("Sidebar.myConfigs")}
                </PatternlibMainNavigationItem>
                <PatternlibMainNavigationItem onClick={toPage(1)}>
                  {t("Sidebar.contact")}
                </PatternlibMainNavigationItem>
              </>
            ) : (
              <Sidebar activeTab={0}></Sidebar>
            )}
          </div>
        </div>
        <div className={styles.panel}>
          <div
            className={`${styles.panelContent} ${
              isDemoUser && styles.reducePadding
            }`}
          >
            <BackButton preventGoingBack={false} url="/myconfigs" />
            <Separator space="xxxxxxs"></Separator>
            <h2>{t("compare.compareTitle")}</h2>
            {values.length > 0 && getListItems()}
            <PatternlibToast
              position="top-right"
              ref={toastRef}
              width={screen === "isMobile" ? "256px" : "408px"}
            ></PatternlibToast>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Compare;
